import { createEffect, createSignal, createUniqueId, type JSX, onCleanup, Show } from 'solid-js';
import { isTopLayer, popLayer, pushLayer } from '../lib/dialog-trap';

// Anchored popover primitive (#5205) — the one outside-click + Escape hygiene
// the anchored panels share: ChangedByChip, ModelPicker, Select's desktop
// branch, OrgSwitcher, RolePill, NotificationBell (on 2026-10-02 no other
// component binds a document `click`/`mousedown` or sets `aria-haspopup="true"`).
// OrgSwitcher, RolePill and NotificationBell each
// hand-rolled their own until plan 101 slice 19 (19-6) moved them onto this:
// all three dropped focus on <body> at Escape, and the bell's Escape never
// asked whether a dialog sat above it. NOT one of the modal shells
// (`testing/overlay-shells.ts`): no `role="dialog"`/`aria-modal`, and it never
// calls `useDialogTrap` — a non-modal menu-style panel does not take the
// keyboard hostage the way a dialog does, and `overlay-shells.test.ts` would
// fail the day it grew either. What it adds over a hand-rolled menu: focus
// returns to the trigger on every close path (Esc, an outside click, or a
// selection inside the panel calling the `close` it is handed).
//
// Controlled internally (own `open` signal), matching the existing pattern:
// each caller mounts its own `<Popover/>` rather than sharing one global
// open slot, so two popovers on a page never fight over it.
//
// Focus restore is conditional, not unconditional: the cleanup below only
// pulls focus back to `trigger` when it has nowhere better to be (nothing
// focused, focus on `document.body`, or focus still inside this popover's own
// mounted root — e.g. the trigger itself, or a panel control that ran `close`
// without moving focus first). If focus already moved somewhere deliberate —
// the user opened a second popover, or clicked into a live input — the
// restore stays out of the way.
//
// Escape is caught on the BUBBLE phase on `document`, same as a dialog
// trap's own listener (`lib/dialog-trap.ts`). A capture-phase listener was
// tried (#5205 review) so this Popover would get first refusal on every
// Escape before an ancestor dialog's trap ever saw the key, but capture on
// `document` runs BEFORE the event ever reaches a control inside this
// panel — so a nested control that means to consume Escape itself (a
// combobox closing its own list, e.g. #5209's model picker) cannot: its own
// `preventDefault()` hasn't run yet by the time the capture listener reads
// `event.defaultPrevented` (round 4, reviewer-verified). In the bubble
// phase, that inner control's own handler — an element listener at the
// target, or Solid's delegated `keydown` listener registered at module
// load — always runs first in the same dispatch, so this listener's
// `event.defaultPrevented` check (below) can see it.
//
// This listener only consumes Escape — `preventDefault()` + `close()` —
// when two things are both true: nothing inside the panel already handled
// the key (`event.defaultPrevented`), and it registers as the topmost layer
// on `lib/dialog-trap.ts`'s shared ownership stack (the same stack every
// modal trap pushes onto). `isTopLayer` alone decides which stacked layer
// owns Escape — a Popover nested inside an open ActionDrawer/Modal/etc. is
// that topmost layer, so it takes the key. The drawer's trap listener is on
// `document` too and was registered first, so it runs first (same-node
// listeners fire in registration order) and backs off on its own
// `isTopLayer` check — it never sees this listener's `preventDefault()`.
//
// Round 3: owning the key also has to mean NOT acting when a dialog opens
// OVER an already-open Popover (the ⌘K palette, the `?` sheet, a
// ConfirmDialog opened from a panel item — none of which click anything).
// `isTopLayer` is what makes that possible: when a dialog trap's layer sits
// above this one, the check fails, this listener is a no-op, and the key
// falls through, bubble phase, to that trap's own listener — which is what
// closes it, leaving the Popover untouched underneath.

export type PopoverProps = {
  /**
   * Accessible name for the trigger button. Required unless {@link namedByField}
   * is true, in which case it is unused (an external `label[for]` names the
   * trigger instead) and may be omitted.
   */
  readonly label?: string;
  /** Trigger button content — icon, text, or both. */
  readonly trigger: JSX.Element;
  /** Extra class on the root wrapper (the positioning anchor for the panel). */
  readonly class?: string;
  /** Extra class on the trigger button. */
  readonly triggerClass?: string;
  /** The trigger's hover title, when it says less than its accessible name (#6785). */
  readonly triggerTitle?: string;
  /** Extra class on the panel. */
  readonly panelClass?: string;
  /**
   * What the trigger says it opens (`aria-haspopup`). `menu` is for a panel that IS an ARIA
   * menu (`role="menu"` with `menuitem` rows and their own arrow keys): the Fleet card menu
   * (#6785). Defaults to `true`, every other caller's.
   */
  readonly haspopup?: 'true' | 'menu';
  /**
   * True while a write the panel started is in flight: the trigger is disabled and
   * `aria-busy`, so it cannot open a second one. An open panel stays open; its caller
   * owns ignoring a pick made there (a disabled trigger cannot stop that click).
   */
  readonly busy?: boolean;
  /**
   * The trigger is disabled independent of {@link busy} — no `aria-busy`, just a
   * plain disabled control (e.g. a `Select` whose caller passed `disabled`).
   * `ModelPicker` and `ChangedByChip` never pass this; their trigger's disabled
   * state is `busy` alone, unchanged.
   */
  readonly triggerDisabled?: boolean | undefined;
  /**
   * A `Field`-scaffolded caller (`Select`, #5620 slice 02) sets `id` /
   * `aria-describedby` / `aria-invalid` on the trigger itself — it IS the
   * labelled control, via `label[for=id]` — and sets {@link namedByField} so
   * `aria-label` is omitted rather than competing with that association
   * (`aria-label` wins over `label[for]` in the accessible-name computation,
   * which would make `getByLabelText` pass without the trigger actually being
   * named by the field's own label).
   */
  readonly triggerId?: string;
  readonly triggerDescribedBy?: string | undefined;
  readonly triggerInvalid?: true | undefined;
  /** See {@link triggerId}. Defaults to false: every existing caller keeps its
   * `aria-label` exactly as before. */
  readonly namedByField?: boolean;
  /**
   * Panel content, rendered only while open. Receives `close` so a selection
   * inside the panel can close it after acting (mirrors `choose` in
   * OrgSwitcher / RolePill, generalized).
   */
  readonly children: (close: () => void) => JSX.Element;
};

export function Popover(props: PopoverProps): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const panelId = createUniqueId();
  let root: HTMLDivElement | undefined;
  let trigger: HTMLButtonElement | undefined;

  const close = (): void => {
    setOpen(false);
  };

  // Outside-click + Escape hygiene, wired only while open. This effect's
  // cleanup runs on every path out of `open` (Esc, outside click, or a
  // `close()` call from inside the panel), so it is the one place that
  // restores focus for all three — but only when focus has nowhere better
  // to be. Two popovers on one page is the case this guards: closing A
  // because the user opened B must not yank focus off B's trigger.
  createEffect(() => {
    if (!open()) return;
    const layer = pushLayer();
    // An inside click is judged in the CAPTURE pass, before any handler can
    // answer it. A panel that swaps its own body in response to a click
    // (#6740's menu-local "Jobs at once" row) detaches the node the click
    // landed on — so by the time a document bubble listener runs, neither
    // `root.contains(target)` nor happy-dom's call-time `composedPath()` can
    // still see that the click was inside. The document capture listener
    // clears the verdict first (it runs above everything); the root capture
    // listener sets it iff the event's path crosses this popover; the bubble
    // listener reads the verdict and closes only a click that stayed outside.
    let landedInside = false;
    const onDocCapture = (): void => {
      landedInside = false;
    };
    const onRootCapture = (): void => {
      landedInside = true;
    };
    const onDocClick = (): void => {
      if (!landedInside) close();
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || !isTopLayer(layer)) return;
      e.preventDefault();
      close();
    };
    document.addEventListener('click', onDocCapture, true);
    root?.addEventListener('click', onRootCapture, true);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => {
      document.removeEventListener('click', onDocCapture, true);
      root?.removeEventListener('click', onRootCapture, true);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      popLayer(layer);
      const active = document.activeElement;
      const nowhereBetter =
        active === null || active === document.body || (root?.contains(active) ?? false);
      if (nowhereBetter) trigger?.focus({ preventScroll: true });
    });
  });

  return (
    <div class={`popover ${props.class ?? ''}`.trim()} ref={root}>
      <button
        type="button"
        id={props.triggerId}
        class={`popover__trigger ${props.triggerClass ?? ''}`.trim()}
        ref={trigger}
        title={props.triggerTitle}
        aria-haspopup={props.haspopup ?? 'true'}
        aria-expanded={open()}
        aria-controls={open() ? panelId : undefined}
        aria-label={props.namedByField === true ? undefined : props.label}
        aria-describedby={props.triggerDescribedBy}
        aria-invalid={props.triggerInvalid}
        disabled={props.triggerDisabled === true || props.busy === true}
        aria-busy={props.busy === true ? 'true' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {props.trigger}
      </button>
      <Show when={open()}>
        <div id={panelId} class={`popover__panel ${props.panelClass ?? ''}`.trim()}>
          {props.children(close)}
        </div>
      </Show>
    </div>
  );
}
